import type { Suit } from "@solitaire/game-engine";
import { useGameStore } from "../store/useGameStore.js";
import { useCardSet } from "../contexts/CardSetContext.js";
import { useFlyAnimation } from "../contexts/FlyAnimationContext.js";

// ============================================================
// FoundationPile — Foundation (1 von 4)
// ============================================================

const SUIT_SYMBOLS: Record<Suit, string> = {
  spades:   "♠",
  hearts:   "♥",
  diamonds: "♦",
  clubs:    "♣",
};

const FOUNDATION_SUITS: Suit[] = ["clubs", "hearts", "spades", "diamonds"];

interface FoundationPileProps {
  foundationIndex: number;
}

export function FoundationPile({ foundationIndex }: FoundationPileProps) {
  const { gameState, selectedCards, handleFoundationClick } = useGameStore();
  const { getFaceUrl } = useCardSet();
  const { isFlying } = useFlyAnimation();

  const pile    = gameState.foundation[foundationIndex]!;
  const topCard = pile[pile.length - 1];
  const belowCard = pile.length >= 2 ? pile[pile.length - 2] : undefined;
  const hasSelection = selectedCards !== null && selectedCards.length === 1;
  const hintSuit = FOUNDATION_SUITS[foundationIndex]!;
  const topIsFlying = topCard ? isFlying(topCard.id) : false;

  return (
    <div
      className={[
        "relative w-full",
        hasSelection ? "cursor-pointer" : "cursor-default",
      ]
        .filter(Boolean)
        .join(" ")}
      style={{ aspectRatio: "2/3" }}
      // Drop-Target für Drag & Drop
      data-droptarget={`foundation-${foundationIndex}`}
      onClick={() => hasSelection && handleFoundationClick(foundationIndex)}
      role={hasSelection ? "button" : "img"}
      aria-label={`Foundation ${foundationIndex + 1}: ${topCard ? `${topCard.rank} of ${topCard.suit}` : "leer"}`}
    >
      {/* Karte darunter zeigen wenn topCard fliegt */}
      {belowCard && topIsFlying && (
        <div className="absolute inset-0">
          <img
            src={getFaceUrl(belowCard)}
            alt={`${belowCard.rank} of ${belowCard.suit}`}
            className="w-full h-full object-fill rounded-md block pointer-events-none"
            draggable={false}
          />
        </div>
      )}
      {topCard ? (
        <div
          data-card-id={topCard.id}
          className="absolute inset-0"
          style={{ opacity: topIsFlying ? 0 : undefined }}
        >
          <img
            src={getFaceUrl(topCard)}
            alt={`${topCard.rank} of ${topCard.suit}`}
            className="w-full h-full object-fill rounded-md block pointer-events-none"
            draggable={false}
          />
        </div>
      ) : (
        <div className="absolute inset-0 rounded-md border-2 border-white/25 flex items-center justify-center">
          <span className="text-white/30 text-2xl leading-none">
            {SUIT_SYMBOLS[hintSuit]}
          </span>
        </div>
      )}
    </div>
  );
}
